import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { ProductType } from "../models/ProductType";
import { useTableData } from "../hooks/useTableData";

interface ProductTypesTableProps {
	productTypes: ProductType[];
	selectedProductType: ProductType | null;
	onRowSelected: (productType: ProductType | null) => void;
	onDelete: (productType: ProductType) => void;
}

export const ProductTypesTable = ({
	productTypes,
	selectedProductType,
	onRowSelected,
	onDelete,
}: ProductTypesTableProps) => {
	const gridRef = useRef<AgGridReact<ProductType>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback(
		(_params: GridReadyEvent<ProductType>) => {},
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<ProductType>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const getRowId = useCallback((params: { data: ProductType }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<ProductType>
			ref={gridRef}
			rowData={productTypes}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={onRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressRowClickSelection={false}
			suppressContextMenu
			alwaysShowVerticalScroll
			alwaysShowHorizontalScroll
			suppressColumnVirtualisation
			animateRows={false}
			rowHeight={36}
			headerHeight={36}
			getRowStyle={(params) => {
				if (selectedProductType?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
